import { jsPDF } from "jspdf";
import GIF from "gif.js.optimized";
import * as UTIF from "utif";
import ImageProps, { DrawingLayerData, LayerProps, TextLayerData } from "../../types/imageStudio/images";
import { fileToBase64, fileToCanvas, urlToFile } from "../fileHandling";
import { canvasToFile } from "./imagesFunctions";
import { Config } from "../../config/Config";
import { fetchWithAuth } from "../fetchWithAuth";
import { exportMedia } from "../exportMedia";
import { ApiPortalBackend } from "../../apis/ApiPortalBackend";

export type downloadScaleTypes = '1x' | '2x' | '4x';
export const downloadScaleList: downloadScaleTypes[] = ['1x', '2x', '4x' ];
export type ExportFormat = "svg" | "pdf" | "jpg" | "png" | "gif" | "tif";
export const aspectRatioList = ['1:1 (quadratic)', '16:9 (landscape)', '9:16 (portait)','4:3 (standard)'];
type aspectRatioTypes = '1:1 (quadratic)' | '16:9 (landscape)' | '9:16 (portait)' | '4:3 (standard)';

export interface DownloadImageOptions {
    image: ImageProps;
    format: ExportFormat;
    layers?: LayerProps[];
    fileName?: string;
    backgroundColor?: string | null;
    gifWorkerPath?: string;
    exportFormat?: downloadScaleTypes;
};

const DEFAULT_GIF_WORKER = "/gif.worker.js";

export const valueIsAspectRatioType= (val:string): val is aspectRatioTypes => {
    return aspectRatioList.includes(val);
}

export type qualityListType = 'HD'| 'Standard'| 'Ultra HD';

export const getImagePixelSize = (aspectRatio:aspectRatioTypes, qualityList:qualityListType='Standard') => {
    let imageDimension = {width:100,height:100};
    switch(aspectRatio){
        case '1:1 (quadratic)': {
            if(qualityList === 'Standard'){
                imageDimension.width = 640;
                imageDimension.height = 640;
            }
            else if(qualityList === 'HD'){
                imageDimension.width = 960;
                imageDimension.height = 960;
            }
            else if(qualityList === 'Ultra HD'){
                imageDimension.width = 1280;
                imageDimension.height = 1280;
            }
           
            break;
        }
        case '9:16 (portait)': {
            if(qualityList === 'Standard'){
                imageDimension.width = 480;
                imageDimension.height = 848;
            }
            else if(qualityList === 'HD'){
                imageDimension.width = 720;
                imageDimension.height = 1280;
            }
            else if(qualityList ==='Ultra HD'){
                imageDimension.width = 900;
                imageDimension.height = 1600;
            }
            break;
        }
        case '16:9 (landscape)': {
            if(qualityList === 'Standard'){
                imageDimension.width = 848;
                imageDimension.height = 480;
            }
            else if(qualityList === 'HD'){
                imageDimension.width = 1280;
                imageDimension.height = 720;
            }
            else if(qualityList === 'Ultra HD'){
                imageDimension.width = 1600;
                imageDimension.height = 900;
            }
            break;
        }
        case '4:3 (standard)': {
            if(qualityList === 'Standard'){
                imageDimension.width = 640;
                imageDimension.height = 480;
            }
            else if(qualityList === 'HD'){
                imageDimension.width = 960;
                imageDimension.height = 720;
            }
            else if(qualityList === 'Ultra HD'){
                imageDimension.width = 1280;
                imageDimension.height = 960;
            }
            break;
        }
        default:{
            imageDimension.width = 960;
            imageDimension.height = 960;
            break;
        }
    }
    return imageDimension;
};

const isFiniteNumber = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);

const sanitizeFileName = (value?: string | null) => {
    const base = (value || "image").trim() || "image";

    return base
        .replace(/\.[^/.]+$/, "") // remove file extension
        .replace(/[\\/:*?"<>|]+/g, "_");
};

const createDownloadFileName = (
    value?: string | null
) => {
    const baseName = sanitizeFileName(value);

    const uuid =
        typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : Math.random().toString(36).slice(2);

    return `${baseName}-${uuid}`;
};

const parseFontSize = (fontSize?: string, fallback = 24) => {
    if (!fontSize) return fallback;
    const parsed = parseFloat(fontSize);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const escapeXml = (value: string) =>
    value
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");


export const loadImage = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = src;
    });

export const fileToDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
export const textLayerToSvgSrc = (width:number, height: number, textLayerData: TextLayerData) => {
    const layerWidth = Math.max(1, Math.round(width || 50));
    const layerHeight = Math.max(1, Math.round(height || 50));
    const text = textLayerData.text || "Text";

    const escapeHtml = (value: string) =>
        value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${layerWidth}" height="${layerHeight}" viewBox="0 0 ${layerWidth} ${layerHeight}">
            <rect width="100%" height="100%" fill="${textLayerData.backgroundColor || "transparent"}"/>
            <foreignObject x="0" y="0" width="${layerWidth}" height="${layerHeight}">
                <div xmlns="http://www.w3.org/1999/xhtml"
                    style="
                        width:100%;
                        height:100%;
                        box-sizing:border-box;
                        padding:6px;
                        display:flex;
                        align-items:center;
                        justify-content:center;
                        overflow:hidden;
                        color:${textLayerData.fontColor || "#ffffff"};
                        font-family:${textLayerData.fontFamily || "Arial"};
                        font-size:${textLayerData.fontSize || "24px"};
                        font-weight:${textLayerData.style?.includes("bold") ? "bold" : "normal"};
                        font-style:${textLayerData.style?.includes("italic") ? "italic" : "normal"};
                        text-decoration:${textLayerData.style?.includes("underline") ? "underline" : "none"};
                        line-height:1.2;
                        text-align:center;
                        white-space:pre-wrap;
                        overflow-wrap:anywhere;
                        word-break:break-word;
                    "
                >${escapeHtml(text)}</div>
            </foreignObject>
        </svg>
    `;

    const src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    return src;
}
const ensureImageSrc = async (img?: ImageProps | null): Promise<string | null> => {
    if (!img) return null;
    if (img.src) return img.src;
    if (img.file) return fileToDataUrl(img.file);
    return null;
};

const getLayerRenderableImage = (layer: LayerProps): ImageProps | null => {
    if (layer.type === "image") {
        return layer.imageEdit || layer.imageData || null;
    }

    if (layer.type === "ai-variation") {
        if (layer.imageEdit) return layer.imageEdit;
        if (layer.imageData) return layer.imageData;
        if (layer.variations?.length) return layer.variations[0];
    }

    return null;
};

const sortVisibleLayers = (layers?: LayerProps[]) => {
    return [...(layers || [])]
        .filter((layer) => !layer.hidden)
        .sort((a, b) => {
            const aNum = Number(a.orderNumber);
            const bNum = Number(b.orderNumber);

            if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
                return aNum - bNum;
            }

            return String(a.orderNumber).localeCompare(String(b.orderNumber), undefined, {
                numeric: true,
                sensitivity: "base"
            });
        });
};

const applyLayerTransform = (
    ctx: CanvasRenderingContext2D,
    layer: LayerProps,
    extraRotation = 0
) => {
    const rotation = ((layer.rotation ?? 0) + extraRotation) * (Math.PI / 180);
    const flipX = layer.flipHorizontal ? -1 : 1;
    const flipY = layer.flipVertical ? -1 : 1;

    ctx.translate(layer.position.x + layer.width / 2, layer.position.y + layer.height / 2);
    ctx.scale(flipX, flipY);

    if (rotation !== 0) {
        ctx.rotate(rotation);
    }
    ctx.translate(-layer.width / 2, -layer.height / 2);
};

const roundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    radius = 0
) => {
    if (radius <= 0) {
        ctx.rect(x, y, w, h);
        return;
    }

    const r = Math.min(radius, w / 2, h / 2);
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
};

const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
): string[] => {
    const paragraphs = text.split("\n");
    const lines: string[] = [];

    paragraphs.forEach((paragraph) => {
        const words = paragraph.split(/\s+/).filter(Boolean);

        if (!words.length) {
            lines.push("");
            return;
        }

        let currentLine = words[0];

        for (let i = 1; i < words.length; i++) {
            const testLine = `${currentLine} ${words[i]}`;
            const width = ctx.measureText(testLine).width;

            if (width <= maxWidth) {
                currentLine = testLine;
            } else {
                lines.push(currentLine);
                currentLine = words[i];
            }
        }

        lines.push(currentLine);
    });

    return lines;
};

const fitFontSizeToBox = (
    ctx: CanvasRenderingContext2D,
    textData: TextLayerData,
    width: number,
    height: number
) => {
    let fontSize = parseFontSize(textData.fontSize, 24);
    const isBold = textData.style?.includes("bold");
    const isItalic = textData.style?.includes("italic");

    if (!textData.autoFit) {
        ctx.font = `${isItalic ? "italic " : ""}${isBold ? "bold " : ""}${fontSize}px ${textData.fontFamily || "Arial"}`;
        return fontSize;
    }

    while (fontSize > 6) {
        ctx.font = `${isItalic ? "italic " : ""}${isBold ? "bold " : ""}${fontSize}px ${textData.fontFamily || "Arial"}`;
        const lines = wrapText(ctx, textData.text || "", Math.max(1, width - 12));
        const lineHeight = fontSize * 1.2;
        const totalHeight = lines.length * lineHeight + 12;

        if (totalHeight <= height) {
            return fontSize;
        }

        fontSize -= 1;
    }

    return 6;
};

const drawTextLayer = (
    ctx: CanvasRenderingContext2D,
    layer: LayerProps,
    textData: TextLayerData
) => {
    ctx.save();
    applyLayerTransform(ctx, layer, textData.rotation ?? 0);

    const isBold = textData.style?.includes("bold");
    const isItalic = textData.style?.includes("italic");
    const isUnderline = textData.style?.includes("underline");

    const fontSize = textData.autoFit
        ? fitFontSizeToBox(ctx, textData, layer.width, layer.height)
        : parseFontSize(textData.fontSize, 16);
    const padding = 6;
    const lineHeight = fontSize * 1.2;

    if (
        textData.backgroundColor &&
        textData.backgroundColor !== "transparent" &&
        (textData.backgroundOpacity ?? 0) > 0
    ) {
        ctx.save();
        ctx.globalAlpha = textData.backgroundOpacity ?? 1;
        ctx.fillStyle = textData.backgroundColor;
        ctx.fillRect(0, 0, layer.width, layer.height);
        ctx.restore();
    }

    if (
        (textData.borderStyle ?? "none") !== "none" &&
        (textData.borderWidth ?? 0) > 0 &&
        (textData.borderOpacity ?? 0) > 0
    ) {
        ctx.save();
        ctx.globalAlpha = textData.borderOpacity ?? 1;
        ctx.strokeStyle = textData.borderColor || "#ffffff";
        ctx.lineWidth = textData.borderWidth ?? 1;
        ctx.lineJoin = "round";

        if (textData.borderStyle === "dashed") {
            ctx.lineCap = "butt";
            ctx.setLineDash([12, 4]);
        } else if (textData.borderStyle === "dotted") {
            ctx.lineCap = "round";
            const dotSize = Math.max(1, textData.borderWidth ?? 1);
            const gap = Math.max(2, dotSize * 1.8);
            ctx.setLineDash([0, gap]);
        } else {
            ctx.lineCap = "butt";
            ctx.setLineDash([]);
        }

        const borderWidth = textData.borderWidth ?? 1;
        const inset = borderWidth / 2;

        ctx.beginPath();
        ctx.rect(
            inset,
            inset,
            layer.width - borderWidth,
            layer.height - borderWidth
        );
        ctx.stroke();
        ctx.restore();
    }
    ctx.save();
    ctx.globalAlpha = textData.textOpacity ?? 1;
    ctx.font = `${isItalic ? "italic " : ""}${isBold ? "bold " : ""}${fontSize}px ${textData.fontFamily || "Arial"}`;
    ctx.fillStyle = textData.fontColor || "#000000";
    ctx.textBaseline = "top";

    const lines = wrapText(ctx, textData.text || "", Math.max(1, layer.width - padding * 2));
    const totalTextHeight = lines.length * lineHeight;
    const startY = Math.max(padding, (layer.height - totalTextHeight) / 2);

    lines.forEach((line, index) => {
        const metricsWidth = ctx.measureText(line).width;
        const x = Math.max(padding, (layer.width - metricsWidth) / 2);
        const y = startY + index * lineHeight;

        if (y + lineHeight > layer.height - padding) return;

        ctx.fillText(line, x, y);

        if (isUnderline && line) {
            const underlineY = y + fontSize + 1;
            ctx.beginPath();
            ctx.lineWidth = Math.max(1, fontSize / 16);
            ctx.strokeStyle = textData.fontColor || "#000000";
            ctx.moveTo(x, underlineY);
            ctx.lineTo(x + metricsWidth, underlineY);
            ctx.stroke();
        }
    });

    ctx.restore();
    ctx.restore();
};

const drawDrawingLayer = async (
    ctx: CanvasRenderingContext2D,
    layer: LayerProps,
    drawingData: DrawingLayerData
) => {
    const rasterSrc = drawingData.rasterSrc;
    if (!rasterSrc) return;

    const img = await loadImage(rasterSrc);

    ctx.save();
    applyLayerTransform(ctx, layer, drawingData.rotation ?? 0);
    ctx.drawImage(img, 0, 0, layer.width, layer.height);
    ctx.restore();
};

const drawImageLayer = async (
    ctx: CanvasRenderingContext2D,
    layer: LayerProps,
    layerImage: ImageProps
) => {
    const src = await ensureImageSrc(layerImage);
    if (!src) return;

    const img = await loadImage(src);
    const style = layer.imageLayerStyle ?? {};

    ctx.save();
    applyLayerTransform(ctx, layer);

    if (
        style.backgroundColor &&
        style.backgroundColor !== "transparent" &&
        (style.backgroundOpacity ?? 0) > 0
    ) {
        ctx.save();
        ctx.globalAlpha = style.backgroundOpacity ?? 1;
        ctx.fillStyle = style.backgroundColor;
        ctx.fillRect(0, 0, layer.width, layer.height);
        ctx.restore();
    }

    ctx.save();
    ctx.globalAlpha = style.imageOpacity ?? 1;

    const crop = layer.cropRect;
    if (crop && crop.w > 0 && crop.h > 0) {
        ctx.beginPath();
        roundedRect(ctx, 0, 0, layer.width, layer.height);
        ctx.clip();

        ctx.drawImage(
            img,
            crop.x,
            crop.y,
            crop.w,
            crop.h,
            0,
            0,
            layer.width,
            layer.height
        );
    } else {
        ctx.drawImage(img, 0, 0, layer.width, layer.height);
    }
    ctx.restore();

    if (
        (style.borderStyle ?? "none") !== "none" &&
        (style.borderWidth ?? 0) > 0 &&
        (style.borderOpacity ?? 0) > 0
    ) {
        ctx.save();
        ctx.globalAlpha = style.borderOpacity ?? 1;
        ctx.strokeStyle = style.borderColor || "#ffffff";
        ctx.lineWidth = style.borderWidth ?? 1;

        if (style.borderStyle === "dashed") {
            ctx.lineCap = "butt";
            ctx.lineJoin = "miter";
            ctx.setLineDash([20,4]);

            const inset = (style.borderWidth ?? 1) / 2;
            ctx.strokeRect(
                inset,
                inset,
                layer.width - (style.borderWidth ?? 1),
                layer.height - (style.borderWidth ?? 1)
            );
        } else if (style.borderStyle === "dotted") {
            const bw = style.borderWidth ?? 1;
            const inset = bw / 2;
            const left = inset;
            const top = inset;
            const right = layer.width - inset;
            const bottom = layer.height - inset;

            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.setLineDash([0, Math.max(2, bw * 2.2)]);

            ctx.beginPath();
            ctx.moveTo(left, top);
            ctx.lineTo(right, top);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(right, top);
            ctx.lineTo(right, bottom);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(right, bottom);
            ctx.lineTo(left, bottom);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(left, bottom);
            ctx.lineTo(left, top);
            ctx.stroke();
        } else {
            ctx.lineCap = "butt";
            ctx.lineJoin = "miter";
            ctx.setLineDash([]);

            const inset = (style.borderWidth ?? 1) / 2;
            ctx.strokeRect(
                inset,
                inset,
                layer.width - (style.borderWidth ?? 1),
                layer.height - (style.borderWidth ?? 1)
            );
        }

        ctx.restore();
    }

    ctx.restore();
};
const getImageLayerRotation = (layer: LayerProps, layerImage: ImageProps): number => {
    if ("rotation" in layerImage && typeof layerImage.rotation === "number") {
        return layerImage.rotation;
    }

    return layer.rotation ?? 0;
};
export const composeImageWithLayers = async (
    image: ImageProps,
    layers?: LayerProps[],
    backgroundColor: string | null = null
): Promise<HTMLCanvasElement> => {
    const baseSrc = await ensureImageSrc(image);
    if (!baseSrc) {
        throw new Error("Das Basisbild hat keine gültige src/file.");
    }

    const baseImage = await loadImage(baseSrc);
    const width = image.width || baseImage.naturalWidth;
    const height = image.height || baseImage.naturalHeight;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
        throw new Error("Canvas-Kontext konnte nicht erstellt werden.");
    }

    if (backgroundColor) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, width, height);
    }

    ctx.drawImage(baseImage, 0, 0, width, height);

    const visibleLayers = sortVisibleLayers(layers);

    for (const layer of visibleLayers) {
        if (layer.type === "text" && layer.textData) {
            drawTextLayer(ctx, layer, layer.textData);
            continue;
        }

        if (layer.type === "drawings" && layer.drawingData) {
            await drawDrawingLayer(ctx, layer, layer.drawingData);
            continue;
        }

        if (layer.type === "image" || layer.type === "ai-variation") {
            const renderable = getLayerRenderableImage(layer);
            if (renderable) {
                await drawImageLayer(ctx, layer, renderable);
            }
        }
    }

    return canvas;
};

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> =>
    new Promise((resolve, reject) => {
        // eslint-disable-next-line no-restricted-syntax -- Blob wird an exportMedia() zur Kennzeichnung/zum Download uebergeben (siehe downloadPng/downloadJpg unten)
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error(`Blob-Erstellung für ${type} fehlgeschlagen.`));
                return;
            }
            resolve(blob);
        }, type, quality);
    });

const canvasToSvgBlob = async (canvas: HTMLCanvasElement) => {
    // eslint-disable-next-line no-restricted-syntax -- wird in ein SVG eingebettet, das anschliessend via downloadSvg() an exportMedia() geht
    const dataUrl = canvas.toDataURL("image/png");
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}" viewBox="0 0 ${canvas.width} ${canvas.height}">
    <image href="${escapeXml(dataUrl)}" width="${canvas.width}" height="${canvas.height}" />
</svg>`.trim();

    return new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
};

const downloadPng = async (canvas: HTMLCanvasElement, fileName: string) => {
    const blob = await canvasToBlob(canvas, "image/png");
    await exportMedia(blob, fileName);
};

const downloadJpg = async (canvas: HTMLCanvasElement, fileName: string) => {
    const jpgCanvas = document.createElement("canvas");
    jpgCanvas.width = canvas.width;
    jpgCanvas.height = canvas.height;

    const ctx = jpgCanvas.getContext("2d");
    if (!ctx) throw new Error("JPG-Canvas konnte nicht erstellt werden.");

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, jpgCanvas.width, jpgCanvas.height);
    ctx.drawImage(canvas, 0, 0);

    const blob = await canvasToBlob(jpgCanvas, "image/jpeg", 0.95);
    await exportMedia(blob, fileName);
};

const downloadSvg = async (canvas: HTMLCanvasElement, fileName: string) => {
    const blob = await canvasToSvgBlob(canvas);
    await exportMedia(blob, fileName);
};

const downloadPdf = async (canvas: HTMLCanvasElement, fileName: string) => {
    const orientation = canvas.width >= canvas.height ? "landscape" : "portrait";
    const pdf = new jsPDF({
        orientation,
        unit: "pt",
        format: [canvas.width, canvas.height]
    });

    // eslint-disable-next-line no-restricted-syntax -- PDF-Export ueber jsPDF (eigener Downloadpfad via pdf.save(), PDF ist ohnehin kein cProxy-/mark-Format)
    const imgData = canvas.toDataURL("image/png");
    pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
    pdf.save(fileName);
};

const downloadGif = async (
    canvas: HTMLCanvasElement,
    fileName: string,
    workerPath: string
) => {
    const gif = new GIF({
        workers: 2,
        quality: 10,
        workerScript: workerPath,
        width: canvas.width,
        height: canvas.height
    });

    gif.addFrame(canvas, { copy: true, delay: 1000 });

    const blob: Blob = await new Promise((resolve, reject) => {
        let finished = false;

        gif.on("finished", (result: Blob) => {
            finished = true;
            resolve(result);
        });

        gif.on("abort", () => {
            reject(new Error("GIF-Erstellung wurde abgebrochen."));
        });

        gif.on("error", (error: unknown) => {
            reject(
                error instanceof Error
                    ? error
                    : new Error("GIF-Erstellung fehlgeschlagen.")
            );
        });

        try {
            gif.render();
        } catch (error) {
            reject(error);
        }

        setTimeout(() => {
            if (!finished) {
                reject(new Error(`GIF-Worker konnte nicht geladen werden: ${workerPath}`));
            }
        }, 8000);
    });

    await exportMedia(blob, fileName);
};

const downloadTif = async (canvas: HTMLCanvasElement, fileName: string) => {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas-Kontext für TIF fehlt.");

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const rgba = new Uint8Array(imageData.data);

    const buffer = UTIF.encodeImage(rgba, imageData.width, imageData.height);
    const blob = new Blob([buffer], { type: "image/tiff" });
    await exportMedia(blob, fileName);
};

export const downloadImageInFormat = async ({
    image,
    format,
    layers = [],
    fileName='image',
    backgroundColor = null,
    gifWorkerPath = DEFAULT_GIF_WORKER,
    exportFormat='1x'
}: DownloadImageOptions) => {
    const safeBaseName = createDownloadFileName(fileName || image.title || "image");
    const canvas = await composeImageWithLayers(image, layers, format === "jpg" ? "#ffffff" : backgroundColor);
    
    switch (format) {
        case "png":{
            const canvasUpscaled = await upscaleImage(exportFormat,canvas,safeBaseName);
            await downloadPng(canvasUpscaled, `${safeBaseName}.png`);
            return;}

        case "jpg":{
            const canvasUpscaled = await upscaleImage(exportFormat,canvas,safeBaseName);
            await downloadJpg(canvasUpscaled, `${safeBaseName}.jpg`);
            return;}

        case "svg":{
            await downloadSvg(canvas, `${safeBaseName}.svg`);
            return;}

        case "pdf":{
            await downloadPdf(canvas, `${safeBaseName}.pdf`);
            return;}

        case "gif":{
            await downloadGif(canvas, `${safeBaseName}.gif`, gifWorkerPath);
            return;}
        case "tif":{
            await downloadTif(canvas, `${safeBaseName}.tif`);
            return;}

        default:
            throw new Error(`Format ${format} wird nicht unterstützt.`);
    }
};

export const upscaleImage = async (
    type: downloadScaleTypes,
    canvas: HTMLCanvasElement,
    filename: string
): Promise<HTMLCanvasElement> => {
    if (type === "1x") {
        return canvas;
    }

    const baseUrl = Config.json.AiServiceProxyUrl;

    const file = await canvasToFile(canvas, `${filename}.png`);
    const base64Image = await fileToBase64(file);

    if (!base64Image) {
        return canvas;
    }

    const path =
        type === "4x"
            ? "generate/image-upscale-4x"
            : "generate/image-upscale-2x";

    const body = {
        positive_prompt: "",
        image: base64Image
    };

    // upscaleImage() is a plain async function, not a React hook, so useTrackedRun() cannot be
    // called here - the run lifecycle is driven directly via ApiPortalBackend instead.
    const workspaceId = await ApiPortalBackend.GetDefaultWorkspaceId();
    if (!workspaceId) {
        throw new Error("Kein Workspace gefunden - Upscale kann nicht abgerechnet werden.");
    }

    const runResult = await ApiPortalBackend.CreateRun({ workspaceId, type: 'image_gen', estimatedCost: 0 });
    if (!runResult.ok) {
        // Fail closed: never call cProxy's paid upscale endpoint without a tracked run.
        throw new Error(
            runResult.reason === 'insufficient_balance'
                ? "Nicht genug Credits für das Upscaling."
                : "Upscale-Run konnte nicht gestartet werden."
        );
    }
    const runId = runResult.run.id;

    const jobResponse = await fetchWithAuth(`${baseUrl}/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
    });

    if (!jobResponse.ok) {
        await ApiPortalBackend.FailRun(runId, `Upscale job creation failed (HTTP ${jobResponse.status})`);
        return canvas;
    }

    const jobData = await jobResponse.json();
    const jobID = jobData.job_id;

    if (!jobID) {
        await ApiPortalBackend.FailRun(runId, "Upscale job creation returned no job_id");
        return canvas;
    }

    const pollResult = async (): Promise<string | null> => {
        for (let attempt = 0; attempt < 90; attempt++) {
            const statusResponse = await fetchWithAuth(`${baseUrl}/status/${jobID}`);

            if (!statusResponse.ok) {
                return null;
            }

            const statusData = await statusResponse.json();
            const status = statusData.status;

            if (status === "finished") {
                const resultResponse = await fetchWithAuth(`${baseUrl}/result/${jobID}`);

                if (!resultResponse.ok) {
                    return null;
                }

                const resultData = await resultResponse.json();

                if (Array.isArray(resultData) && resultData.length > 0) {
                    return String(resultData[0]).replaceAll("\\", "/");
                }

                return null;
            }

            if (status === "timeout" || status === "error") {
                return null;
            }

            await new Promise((resolve) => setTimeout(resolve, 2000));
        }

        return null;
    };

    const resultUrl = await pollResult();

    if (!resultUrl) {
        await ApiPortalBackend.FailRun(runId, "Upscale polling timed out or failed");
        return canvas;
    }

    const imgFile = await urlToFile(
        resultUrl,
        resultUrl.split("/").slice(-1)[0] || `${filename}.png`
    );

    await ApiPortalBackend.CompleteRun(runId, { cost: 0 });

    return await fileToCanvas(imgFile);
};

export default downloadImageInFormat;

export const convertFileToPngFile = async (
    file: File,
    filename?: string
): Promise<File> => {

    const canvas = await fileToCanvas(file);

    const blob = await canvasToBlob(canvas,"image/png")

    return new File(
        [blob],
        filename ?? file.name.replace(/\.[^/.]+$/, ".png"),
        {
            type: "image/png"
        }
    );
};

export const createImageThumbnailBlob = async (
    image: ImageProps,
    layers: LayerProps[] = [],
    size = 200,
    backgroundColor: string | null = null
): Promise<Blob> => {
    const composedCanvas = await composeImageWithLayers(
        image,
        layers,
        backgroundColor
    );

    const thumbnailCanvas = document.createElement("canvas");
    thumbnailCanvas.width = size;
    thumbnailCanvas.height = size;

    const ctx = thumbnailCanvas.getContext("2d");
    if (!ctx) {
        throw new Error("Thumbnail canvas context could not be created.");
    }

    ctx.clearRect(0, 0, size, size);

    const scale = Math.min(
        size / composedCanvas.width,
        size / composedCanvas.height
    );

    const drawWidth = composedCanvas.width * scale;
    const drawHeight = composedCanvas.height * scale;

    const x = (size - drawWidth) / 2;
    const y = (size - drawHeight) / 2;

    ctx.drawImage(
        composedCanvas,
        x,
        y,
        drawWidth,
        drawHeight
    );

    return await canvasToBlob(thumbnailCanvas, "image/png");
};

export const createImageThumbnailFile = async (
    image: ImageProps,
    layers: LayerProps[] = [],
    filename = "thumbnail.png",
    size = 200,
    backgroundColor: string | null = null
): Promise<File> => {
    const blob = await createImageThumbnailBlob(
        image,
        layers,
        size,
        backgroundColor
    );

    return new File(
        [blob],
        filename,
        { type: "image/png" }
    );
};